import React, { useEffect, useState, useRef } from 'react'
import { Box, Button, Flex, Heading, Text } from 'rebass/styled-components'
import styled from 'styled-components'
import { Link } from 'gatsby'
import renderHTML from 'react-render-html'
import { motion } from 'framer-motion'
import EnlargeIcon from '../assets/icons/enlarge.svg'
import AnimationWrapper from '../helpers/Animations'
import MediaRender from '../components/general/MediaRender'
import SwiperFilmStrip from '../components/swiper/FilmStrip'
import FeaturedPublication from './FeaturedPublication'

const HorizontalRuler = styled(Box)`
    background-color: transparent;
    border-color: ${(props) => props.theme.colors.aquaDark};
    border-style: solid;
    border-width: 1px 0 3px 0;
    display: block;
    height: 8px;
    margin-top: 0;
    margin-right: 0;
    margin-bottom: 4px;
    margin-left: 0;
    padding: 0;
`

const FeaturedCallToAction = styled(Link)`
    color: ${(props) => props.theme.colors.black};
    cursor: pointer;
    display: inline-block;
    font-family: ${(props) => props.theme.fonts.body};
    font-size: ${(props) => props.theme.fontSizes.md};
    font-weight: ${(props) => props.theme.fontWeights.semibold};
    text-decoration: none;
    overflow: hidden;
    padding: 0 0 5px 0;
    position: relative;
    &::before,
    &::after {
        background-color: ${(props) => props.theme.colors.black};
        content: '';
        display: inline-block;
        height: 2px;
        left: 0;
        opacity: 1;
        position: absolute;
        bottom: 0;
        width: 100%;
        z-index: 0;
    }
    &::before {
        background-color: ${(props) => props.theme.colors.white};
        z-index: 1;
        transform: translateX(-100%);
        transition-duration: 350ms;
        transition-timing-function: cubic-bezier(0.77, 0, 0.175, 1);
        transition-property: transform;
    }

    &:hover::before {
        transform: translateX(0);
    }
`

const NarrativeContent = styled(Box)`
    a {
        background-color: transparent;
        background-image: linear-gradient(to right, #77b7be 0, #77b7be 100%);
        background-position: 0 100%;
        background-repeat: no-repeat;
        background-size: 0 2px;
        border-color: ${(props) => props.theme.colors.black};
        border-style: solid;
        border-width: 0 0 2px 0;
        color: ${(props) => props.theme.colors.black};
        cursor: pointer;
        display: inline;
        padding-bottom: 1px;
        padding-top: 1px;
        will-change: background-size;
        text-decoration: none;

        &:hover {
            background-size: 100% 2px;
            border-color: transparent;
            padding-bottom: 3px;
            transition-duration: 400ms;
            transition-property: background-size;
            transition-timing-function: cubic-bezier(0.2, 0.75, 0.5, 1);
        }
    }
`

const Badge = styled(Box)`
    align-items: center;
    border-radius: 100%;
    color: ${(props) => props.theme.colors.aquaLight};
    display: flex;
    font-family: ${(props) => props.theme.fonts.heading};
    font-size: 28px;
    height: 82px;
    justify-content: center;
    line-height: 22px;
    position: absolute;
    right: 6px;
    text-align: center;
    top: -43px;
    width: 82px;
    z-index: 1;
    @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        font-size: 14px;
        height: 64px;
        line-height: 18px;
        top: -33px;
        width: 64px;
    }
`

const FeaturedDesktop = ({ firstModule, options, data }) => {
    let content = null
    const badge = data?.categories?.includes('sold') ? (
        <Badge bg='aquaDark'>Sold</Badge>
    ) : null
    const textContainer = useRef(null)
    const [media, updateMedia] = useState({
        el: false,
        enlarged: false,
        carousel: false,
    })

    useEffect(() => {
        if (options?.media?.set?.length) {
            const update = { ...media }
            update.el = (
                <MediaRender
                    file={options.media.set[0]}
                    alt={options.title}
                    options={{
                        overlay: true,
                        coverImage: options.coverImage?.set[0] || false,
                    }}
                />
            )
            update.enlarged = options.enlarged || false
            update.type = options.media.set[0]?.media_type
            update.carousel =
                options.carouselSource === 'gallery'
                    ? data.media.gallery?.images
                    : options.media.set
            updateMedia(update)
        }
    }, [options, data])

    const switchImage = (slide, enlarge) => {
        const update = { ...media }
        update.el = (
            <MediaRender
                file={slide}
                alt={options.heading}
                options={{ overlay: true }}
            />
        )
        if (enlarge) {
            update.enlarged = enlarge
        }
        updateMedia(update)
    }
    const toggleEnlarge = () => {
        const update = { ...media }
        update.enlarged = !media.enlarged
        updateMedia(update)
    }

    switch (options.type) {
        case 'publication':
            content = <FeaturedPublication options={options} />
            break
        default:
            let justifyContent
            let extraTopSpace
            let mediaWidth

            switch (options.variation) {
                case 'secondary':
                    justifyContent = options.reverse
                        ? 'space-between'
                        : 'flex-end'
                    extraTopSpace = ''
                    mediaWidth = '55%'
                    break

                default:
                    justifyContent = 'flex-end'
                    extraTopSpace = 'md'
                    mediaWidth = '65%'
                    break
            }

            const animateText = {
                enlarged: {
                    marginLeft:
                        (textContainer.current &&
                            -textContainer.current.clientWidth) ||
                        0,
                    opacity: 0,
                },
                initial: {
                    marginLeft: 0,
                    opacity: 1,
                    width: '36%',
                },
            }
            const animateMedia = {
                enlarged: {
                    width: '100%',
                },
                initial: {
                    width: mediaWidth,
                },
            }

            content = (
                <Box>
                    {options.showBorderRipple ? (
                        <>
                            {' '}
                            <HorizontalRuler as='hr' />{' '}
                        </>
                    ) : null}
                    <Box
                        bg={options.backgroundColor}
                        px='md'
                        pt={firstModule ? 'xxxl' : 'lg'}
                        pb='lg'
                    >
                        <Box
                            pt='xs'
                            variant='container'
                        >
                            {options.showHeadingSection ? (
                                <>
                                    <Flex
                                        alignItems='flex-start'
                                        pt='md'
                                        pb={options.subheading ? 'lg' : 'sm'}
                                    >
                                        <Box
                                            alignSelf='flex-start'
                                            flexGrow='1'
                                        >
                                            <AnimationWrapper type='fade-left'>
                                                <Heading
                                                    as='h2'
                                                    color='black'
                                                    variant='h1'
                                                >
                                                    {options.heading}
                                                </Heading>
                                            </AnimationWrapper>
                                        </Box>

                                        <Box alignSelf='flex-start'>
                                            <Flex
                                                alignItems='flex-end'
                                                justifyContent='flex-end'
                                            >
                                                {options.showForSaleButton ? (
                                                    <Box
                                                        alignSelf='center'
                                                        mr='sm'
                                                    >
                                                        <Link to='/cars-for-sale/available'>
                                                            <Button variant='primary_aquaDark'>
                                                                All Available
                                                            </Button>
                                                        </Link>
                                                    </Box>
                                                ) : null}
                                                {options.showSoldButton ? (
                                                    <Box alignSelf='center'>
                                                        <Link to='/cars-for-sale/sold'>
                                                            <Button variant='secondary_aquaDark'>
                                                                Previously
                                                                Handled
                                                            </Button>
                                                        </Link>
                                                    </Box>
                                                ) : null}
                                                {options.showConsignmentButton ? (
                                                    <Box alignSelf='center'>
                                                        <Link to='/consignment'>
                                                            <Button variant='secondary_aquaDark'>
                                                                Consign Your Car
                                                            </Button>
                                                        </Link>
                                                    </Box>
                                                ) : null}
                                            </Flex>
                                        </Box>
                                    </Flex>
                                    {options.subheading ? (
                                        <Box
                                            pb='xxs'
                                            sx={{
                                                borderColor: 'black',
                                                borderStyle: 'solid',
                                                borderWidth: '0 0 1px 0',
                                            }}
                                        >
                                            <Heading
                                                as='h3'
                                                variant='h3'
                                                sx={{
                                                    fontSize: '16px',
                                                    fontWeight: '700',
                                                    letterSpacing: '4px',
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                {options.subheading}
                                            </Heading>
                                        </Box>
                                    ) : null}
                                </>
                            ) : null}

                            <Flex
                                flexDirection={[
                                    'column',
                                    'column',
                                    'column',
                                    options.reverse ? 'row-reverse' : 'row',
                                ]}
                                alignItems={
                                    options.verticalAlign || 'flex-start'
                                }
                                justifyContent={justifyContent}
                                pt={extraTopSpace}
                                sx={{
                                    position: 'relative',
                                    overflow: 'visible',
                                }}
                            >
                                {/* {
                                media.type !== 'image' ?
                                    null : */}
                                <motion.div
                                    variants={animateText}
                                    initial='initial'
                                    animate={
                                        media.enlarged ? 'enlarged' : 'initial'
                                    }
                                    ref={textContainer}
                                    order='0'
                                >
                                    <Box
                                        order='0'
                                        pb={
                                            options.additionalText
                                                ? '202px'
                                                : ''
                                        }
                                        pr='xxl'
                                    >
                                        <Heading
                                            as={
                                                options.showHeadingSection &&
                                                firstModule
                                                    ? 'h3'
                                                    : 'h1'
                                            }
                                            variant='h2'
                                            color={
                                                options.titleColor || 'black'
                                            }
                                            sx={{
                                                textTransform: 'uppercase',
                                                lineHeight: '34px',
                                            }}
                                        >
                                            {options.url &&
                                            options.url !== '' ? (
                                                <Link
                                                    to={options.url}
                                                    css={{
                                                        backgroundImage: `linear-gradient(to right,${
                                                            options.titleColor ||
                                                            'black'
                                                        } 0,${
                                                            options.titleColor ||
                                                            'black'
                                                        } 100%)`,
                                                        backgroundPosition:
                                                            '0 100%',
                                                        backgroundRepeat:
                                                            'no-repeat',
                                                        backgroundSize: '0 2px',
                                                        color:
                                                            options.titleColor ||
                                                            'black',
                                                        display: 'inline',
                                                        textDecoration: 'none',
                                                        willChange:
                                                            'background-size',
                                                        '&:hover': {
                                                            backgroundSize:
                                                                '100% 2px',
                                                            transitionDuration:
                                                                '400ms',
                                                            transitionProperty:
                                                                'background-size',
                                                            transitionTimingFunction:
                                                                'cubic-bezier(.2,.75,.5,1.0)',
                                                        },
                                                    }}
                                                >
                                                    {options.title}
                                                </Link>
                                            ) : (
                                                options.title
                                            )}
                                        </Heading>
                                        {options.subtitle ? (
                                            <Text
                                                as='p'
                                                variant='paragraph'
                                                fontStyle='italic'
                                                fontSize='md'
                                            >
                                                {options.subtitle}
                                            </Text>
                                        ) : null}
                                        <NarrativeContent>
                                            <Text
                                                as='p'
                                                variant='paragraph'
                                                fontSize='md'
                                            >
                                                {options.text &&
                                                    renderHTML(options.text)}
                                            </Text>
                                        </NarrativeContent>
                                        {options.url && options.url !== '' ? (
                                            <FeaturedCallToAction
                                                to={options.url}
                                            >
                                                See More
                                            </FeaturedCallToAction>
                                        ) : null}
                                        {options.additionalText &&
                                        options.additionalText !== '' ? (
                                            <Box
                                                sx={{
                                                    bottom: 0,
                                                    left: 0,
                                                    pointerEvents: 'none',
                                                    position: 'absolute',
                                                    zIndex: 1,
                                                }}
                                            >
                                                <Text
                                                    as='span'
                                                    variant='bigText'
                                                >
                                                    {options.additionalText}
                                                </Text>
                                            </Box>
                                        ) : null}
                                    </Box>
                                </motion.div>
                                {/* } */}
                                <motion.div
                                    variants={animateMedia}
                                    initial={
                                        media.enlarged ? 'enlarged' : 'initial'
                                    }
                                    animate={
                                        media.enlarged ? 'enlarged' : 'initial'
                                    }
                                    order={[0, 0, 0, 1]}
                                >
                                    <AnimationWrapper type='fade-up'>
                                        <Box
                                            height='100%'
                                            sx={{
                                                position: 'relative',
                                            }}
                                        >
                                            {media.type !== 'image' ? (
                                                media.el
                                            ) : (
                                                <>
                                                    <Box
                                                        onClick={() =>
                                                            toggleEnlarge(
                                                                !media.enlarged
                                                            )
                                                        }
                                                        sx={{
                                                            backgroundImage: `url(${EnlargeIcon})`,
                                                            backgroundPosition:
                                                                'calc(100% - 12px) 12px',
                                                            backgroundRepeat:
                                                                'no-repeat',
                                                            backgroundSize:
                                                                '18px',
                                                            cursor: 'pointer',
                                                            position:
                                                                'absolute',
                                                            top: '0',
                                                            left: '0',
                                                            width: '100%',
                                                            height: '100%',
                                                            display: 'block',
                                                            zIndex: 1,
                                                        }}
                                                    />
                                                    {badge}
                                                    {media.el}
                                                </>
                                            )}
                                        </Box>
                                    </AnimationWrapper>
                                </motion.div>
                            </Flex>
                        </Box>
                        {options.carousel &&
                        media.carousel &&
                        media.carousel.length > 1 ? (
                            <Box
                                mt='sm'
                                sx={{
                                    overflow: 'hidden',
                                }}
                            >
                                <SwiperFilmStrip
                                    onPress={(slide, enlarge) =>
                                        switchImage(slide, enlarge)
                                    }
                                    options={{
                                        overlay:
                                            options.heading === 'Publications'
                                                ? false
                                                : true,
                                        direction: 'horizontal',
                                        type: 'imageOnly',
                                        disableClick: true,
                                        loop: true,
                                        navigation: true,
                                    }}
                                    slides={{ docs: media.carousel }}
                                />
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            )
    }

    return content
}

export default FeaturedDesktop
